// Client portal dashboard — appears for: (a) Client Admin users by default,
// (b) Internal users who switched into a portal view. Header band adopts the
// client's brand colour so the portal feels native to them.
import { useEffect, useState } from "react";
import {
    FolderOpen, Building, Calendar, CheckSquare, AlertTriangle, FileText,
    ShieldCheck, Layers, ArrowRight, Clock, ArrowUpRight, TrendingUp,
    Palette, ChevronDown,
} from "lucide-react";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { shade } from "@/lib/theme";
import { useAuth } from "@/context/AuthContext";
import KpiCard from "@/components/KpiCard";
import DigestBanner from "@/components/DigestBanner";
import CertExpiryWidget from "@/components/CertExpiryWidget";
import ClientDashboardLanes from "@/components/ClientDashboardLanes";
import PortalBrandingControls from "@/components/PortalBrandingControls";
import { usePageHeader } from "@/context/PageHeaderContext";
import { enabledPortalRouteSet } from "@/lib/portalModules";
import { TID } from "@/lib/testIds";

const STAGE_LABELS = {
    enquiry: "Enquiry", setup: "Setup", pre_construction: "Pre-Con", site_visits: "Site Visits",
    reporting: "Reporting", qa: "QA", client_issue: "Client Issue", completion: "Completion",
};
const STAGE_ORDER = ["enquiry", "setup", "pre_construction", "site_visits", "reporting", "qa", "client_issue", "completion"];
const COMPLIANCE_TONE = { GREEN: "green", AMBER: "amber", RED: "red" };

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); }
    catch { return s; }
}

export default function ClientPortalDashboard({ clientId }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { setHeader } = usePageHeader();
    const [showAppearance, setShowAppearance] = useState(false);

    useEffect(() => {
        if (!clientId) return;
        setData(null); setError(false);
        api.get(`/dashboard/client/${clientId}`)
            .then((r) => setData(r.data))
            .catch(() => setError(true));
    }, [clientId]);

    useEffect(() => {
        if (!data) return;
        const cc = data.client;
        const bits = [cc.sector, `${data.sites_count} sites`, `${data.buildings_count} buildings`];
        if (cc.account_manager) bits.push(`Account manager ${cc.account_manager}`);
        setHeader({ title: "Dashboard", subtitle: bits.filter(Boolean).join(" · ") });
        return () => setHeader(null);
    }, [data, setHeader]);

    if (error) return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Couldn't load this portal. Please refresh or contact your Priority Risk consultant.</div>;
    if (!data) return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading portal…</div>;

    const c = data.client;
    const k = data.kpis;
    const brand = c.branding.primary_colour;
    const secondary = c.branding.secondary_colour;
    const enabled = enabledPortalRouteSet(c);
    const show = (m) => enabled === null || enabled.has(m);
    // Professional branded hero: solid primary deepening to a subtly darker shade
    // of the SAME colour (no blue→yellow switch). Secondary is an accent only.
    const headerGradient = `linear-gradient(135deg, ${brand} 0%, ${shade(brand, -0.18)} 100%)`;
    const mode = user?.mode || "live";
    const BK = process.env.REACT_APP_BACKEND_URL;
    const bannerUrl = c.branding?.banner_url ? `${BK}/api/clients/${c.id}/banner?mode=${mode}` : null;
    const wideLogoUrl = c.branding?.wide_logo_url ? `${BK}/api/clients/${c.id}/wide-logo?mode=${mode}` : null;
    // With a banner, overlay a darkened brand gradient so white text stays legible.
    const headerStyle = bannerUrl
        ? {
            backgroundImage: `linear-gradient(135deg, ${brand}e6 0%, ${shade(brand, -0.18)}cc 55%, rgba(10,15,25,.5) 100%), url("${bannerUrl}")`,
            backgroundSize: "cover", backgroundPosition: "center",
            borderBottom: secondary ? `3px solid ${secondary}` : undefined,
        }
        : { background: headerGradient, borderBottom: secondary ? `3px solid ${secondary}` : undefined };

    return (
        <div data-testid={TID.clientPortalDashboard} className="space-y-7">
            <DigestBanner />

            {/* Appearance / branding editor (client_admin + PR staff) */}
            {user && ["client_admin", "director", "administrator", "consultant"].includes(user.role) && (
                <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }} data-testid="portal-appearance">
                    <button data-testid="portal-appearance-toggle" onClick={() => setShowAppearance((v) => !v)}
                        className="w-full flex items-center gap-2 px-5 py-3 text-left hover:bg-[#FBFAF8]">
                        <Palette className="w-4 h-4" style={{ color: brand }} />
                        <span className="text-[14px] font-semibold text-[#1F1F1F] flex-1">Customise appearance</span>
                        <span className="text-[11.5px] text-[#9A968F]">Fonts, banner &amp; logo</span>
                        <ChevronDown className={`w-4 h-4 text-[#9A968F] transition-transform ${showAppearance ? "rotate-180" : ""}`} />
                    </button>
                    {showAppearance && (
                        <div className="px-5 pb-5 border-t" style={{ borderColor: "#EFEAE3" }}>
                            <PortalBrandingControls clientId={c.id} branding={c.branding}
                                onSaved={(b) => {
                                    setData((d) => ({ ...d, client: { ...d.client, branding: b } }));
                                    window.dispatchEvent(new Event("clients:changed"));
                                }} />
                        </div>
                    )}
                </div>
            )}

            {/* Phase 3 — ownership-split lanes: your team's tasks vs what PR is doing */}
            <ClientDashboardLanes data={data} brand={brand} />

            {/* KPI grid */}
            <div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))" }}>
                {show("projects") && <KpiCard kpiKey="portal-projects"   icon={FolderOpen}    label="Active projects"     value={k.active_projects}    tone="blue" />}
                {show("buildings") && <KpiCard kpiKey="portal-buildings"  icon={Building}      label="Active buildings"    value={k.active_buildings}   tone="purple" />}
                {show("schedule") && <KpiCard kpiKey="portal-visits"     icon={Calendar}      label="Site visits this month" value={k.site_visits_month} tone="green" />}
                {show("actions") && <KpiCard kpiKey="portal-actions"    icon={CheckSquare}   label="Open actions"        value={k.open_actions}       tone="amber" />}
                {show("actions") && <KpiCard kpiKey="portal-overdue"    icon={AlertTriangle} label="Overdue actions"     value={k.overdue_actions}    tone="red" />}
                {show("compliance") && <KpiCard kpiKey="portal-compliance" icon={ShieldCheck}   label="Compliance" value={`${k.compliance_pct}%`} tone={COMPLIANCE_TONE[k.compliance_band] || "green"} />}
                {show("bsc") && k.bsc_completeness != null && (
                    <KpiCard kpiKey="portal-bsc" icon={Layers} label="BSC completeness" value={`${k.bsc_completeness}%`} tone="accent" />
                )}
                {show("reports") && <KpiCard kpiKey="portal-reports" icon={FileText} label="Recent reports" value={k.reports_recent} tone="neutral" />}
            </div>

            {/* Ways to improve the compliance score — live tips, not just the weekly email */}
            {show("compliance") && data.compliance_tips?.length > 0 && (
                <div data-testid="compliance-improvement-tips" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                        <TrendingUp className="w-4 h-4" style={{ color: brand }} />
                        <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Ways to improve your compliance score</h3>
                    </div>
                    <ul className="divide-y" style={{ borderColor: "#F0EDE8" }}>
                        {data.compliance_tips.map((tip, i) => (
                            <li key={i} data-testid={`compliance-tip-${i}`} className="px-5 py-3 text-[12.5px] leading-relaxed text-[#4A4640] flex gap-2.5">
                                <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: brand }} />
                                {tip}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Project pipeline (always visible — this is where projects appear) */}
            {show("projects") && (
                <div data-testid="portal-projects-pipeline" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                        <div className="flex items-center gap-2">
                            <FolderOpen className="w-4 h-4" style={{ color: brand }} />
                            <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Project pipeline</h3>
                        </div>
                        <a href="/app/projects" className="text-[11.5px] font-semibold flex items-center gap-1 hover:underline" style={{ color: brand }}>
                            All projects <ArrowRight className="w-3 h-3" />
                        </a>
                    </div>
                    {/* Stage strip */}
                    <div className="px-5 py-3.5 grid gap-2 border-b" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(78px,1fr))", borderColor: "#F0EDE8" }}>
                        {STAGE_ORDER.map((s) => (
                            <div key={s} data-testid={`portal-stage-${s}`} className="rounded-[8px] border px-2.5 py-2 text-center" style={{ borderColor: "#E5E0DA" }}>
                                <div className="text-[18px] font-bold leading-none" style={{ color: (data.projects_by_stage?.[s] || 0) ? brand : "#C7C2BB" }}>{data.projects_by_stage?.[s] || 0}</div>
                                <div className="text-[9px] font-semibold mt-1 uppercase tracking-wide" style={{ color: "#9A968F" }}>{STAGE_LABELS[s]}</div>
                            </div>
                        ))}
                    </div>
                    {/* Recent project rows */}
                    <ul>
                        {data.projects.map((p) => (
                            <li key={p.id} data-testid={`portal-project-${p.id}`}
                                onClick={() => navigate(`/app/projects/${p.id}`)}
                                className="flex items-center justify-between px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate flex items-center gap-1.5">
                                        {p.health && <span className="w-2 h-2 rounded-full shrink-0" title={`Health: ${p.health}`}
                                            style={{ background: p.health === "red" ? "#B33A2E" : p.health === "amber" ? "#C68A1C" : "#3F7D3A" }} />}
                                        {p.name}
                                    </div>
                                    <div className="text-[10.5px] flex items-center gap-2" style={{ color: "#9A968F" }}>
                                        <span>{p.reference} · {p.lead_consultant || "Unassigned"}</span>
                                        {p.progress_pct != null && (
                                            <span className="inline-flex items-center gap-1">
                                                <span className="w-12 h-1.5 rounded-full overflow-hidden inline-block align-middle" style={{ background: "#EFEAE3" }}>
                                                    <span className="block h-full rounded-full" style={{ width: `${p.progress_pct}%`, background: brand }} />
                                                </span>
                                                {p.progress_pct}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2.5 shrink-0">
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>
                                        {STAGE_LABELS[p.workflow_stage] || p.workflow_stage}
                                    </span>
                                    <span className="text-[11px]" style={{ color: "#6F6A64" }}>{fmtDate(p.target_date)}</span>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-[#C7C2BB]" />
                                </div>
                            </li>
                        ))}
                        {data.projects.length === 0 && (
                            <li className="px-5 py-6 italic text-center text-[#9A968F] text-[12px]">No active projects.</li>
                        )}
                    </ul>
                </div>
            )}

            {/* Action trend — collapsible, default collapsed */}
            {show("actions") && (
                <CollapsibleSection testid="portal-action-trend" icon={TrendingUp} title="Action trend" brand={brand} defaultOpen={false}>
                    <div className="p-5">
                        <p className="text-[11.5px] mb-3" style={{ color: "#6F6A64" }}>Last 6 months · raised vs closed</p>
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={data.action_trend} margin={{ top: 8, right: 6, left: -18, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="pRaised" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={brand} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={brand} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                <XAxis dataKey="month" tick={{ fontSize: 10.5, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 10.5, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E0DA" }} />
                                <Area type="monotone" dataKey="created" name="Raised" stroke={brand} strokeWidth={2} fill="url(#pRaised)" />
                                <Area type="monotone" dataKey="completed" name="Closed" stroke="#3F7D3A" strokeWidth={2} fillOpacity={0} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </CollapsibleSection>
            )}

            {/* Upcoming site visits + Certificates — side by side, narrower */}
            {(show("schedule") || show("certificates")) && (
            <div className="grid lg:grid-cols-2 gap-5 items-start">
                {show("schedule") && (
                <CollapsibleSection testid="portal-upcoming-visits" icon={Calendar} title="Upcoming site visits" brand={brand}
                    count={data.upcoming_visits.length} defaultOpen={true} viewAllHref="/app/schedule" viewAllLabel="Schedule">
                    <ul>
                        {data.upcoming_visits.map((v) => (
                            <li key={v.id} className="flex items-center justify-between px-5 py-3 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 text-center shrink-0">
                                        <div className="text-[15px] font-bold leading-none" style={{ color: brand }}>{fmtDate(v.scheduled_date).split(" ")[0]}</div>
                                        <div className="text-[9.5px] uppercase tracking-wide" style={{ color: "#9A968F" }}>{fmtDate(v.scheduled_date).split(" ")[1]}</div>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{v.title}</div>
                                        <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{v.assignee_name || "Unassigned"}</div>
                                    </div>
                                </div>
                                {v.scheduled_time && (
                                    <span className="inline-flex items-center gap-1 text-[11px] shrink-0" style={{ color: "#6F6A64" }}>
                                        <Clock className="w-3 h-3" /> {v.scheduled_time}
                                    </span>
                                )}
                            </li>
                        ))}
                        {data.upcoming_visits.length === 0 && (
                            <li className="px-5 py-6 italic text-center text-[#9A968F] text-[12px]">No upcoming visits scheduled.</li>
                        )}
                    </ul>
                </CollapsibleSection>
                )}

                {show("certificates") && (
                <CollapsibleSection testid="portal-section-certs" icon={ShieldCheck} title="Certificates expiring soon" brand={brand}
                    defaultOpen={false} viewAllHref="/app/certificates" viewAllLabel="View all">
                    <CertExpiryWidget clientId={c.id} brand={brand} headerless />
                </CollapsibleSection>
                )}
            </div>
            )}
        </div>
    );
}

function CollapsibleSection({ icon: Icon, title, count, brand, defaultOpen = false, viewAllHref, viewAllLabel = "View all", testid, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div data-testid={testid} className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderBottom: open ? "1px solid #F0EDE8" : "1px solid transparent" }}>
                <button data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)}
                    className="flex items-center gap-2 flex-1 text-left min-w-0">
                    <Icon className="w-4 h-4 shrink-0" style={{ color: brand }} />
                    <h3 className="text-[14px] font-semibold text-[#1F1F1F] truncate">{title}</h3>
                    {count != null && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{count}</span>
                    )}
                </button>
                {viewAllHref && open && (
                    <a href={viewAllHref} className="text-[11.5px] font-semibold flex items-center gap-1 hover:underline shrink-0" style={{ color: brand }}>
                        {viewAllLabel} <ArrowRight className="w-3 h-3" />
                    </a>
                )}
                <button data-testid={`${testid}-chevron`} onClick={() => setOpen((o) => !o)} className="shrink-0 p-0.5" aria-label="Toggle section">
                    <ChevronDown className={`w-4 h-4 text-[#9A968F] transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
            </div>
            {open && <div>{children}</div>}
        </div>
    );
}

function PortalLogo({ clientId, logoUrl, initials }) {
    const [broken, setBroken] = useState(false);
    if (logoUrl && !broken) {
        return (
            <div className="w-16 h-16 rounded-[12px] bg-white grid place-items-center overflow-hidden border border-white/25 shadow-lg">
                <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/logo`}
                    alt="Client logo"
                    data-testid="portal-dashboard-logo"
                    onError={() => setBroken(true)}
                    className="w-full h-full object-contain p-1.5"
                />
            </div>
        );
    }
    return (
        <div className="w-16 h-16 rounded-[12px] bg-white/15 grid place-items-center font-serif text-[28px] border border-white/25 backdrop-blur-sm">
            {initials}
        </div>
    );
}


function PortalBadge({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold bg-white/15 backdrop-blur-sm border border-white/25">
            <Icon className="w-3 h-3" /> {label}
        </span>
    );
}
